import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import RoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/RoundedButton';
import { DS_SIZE_MAP } from 'Components/ComponentLibrary/DesignSystem/sizes';
import { Typography } from '@material-ui/core';
import fr from 'facet-rest';
import { GetAppUrl } from 'Consts/sharedUris';
import { clientSchedulerUri } from 'Consts/clientUris';
import { copyToClipboard } from 'Helpers/Utils';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import history from 'State/history';
import { advisorHouseholdSchedulerUri } from 'Consts/advisorUris';
import moment from 'moment-timezone';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import CancelMeetingButton from 'Components/ComponentLibrary/Components/Buttons/CancelMeetingButton';
import ShareRescheduleMeetingLinkButton from 'Components/ComponentLibrary/Components/Buttons/ShareRescheduleMeetingLinkButton';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import { useListContextDispatch } from 'Components/UI/ListDetailView/ListContext';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 350,
    padding: theme.spacing(0, 1),
    '& > *:not(:first-child)': {
      marginBottom: theme.spacing(1),
    },
  },
  actionContainer: {
    display: 'flex',
    justifyContent: 'center',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  meetingSubtitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  meetingSubtitle: {
    color: theme.palette.facet.primaryGray,
    fontSize: 14,
  },
  meetingTitle: {
    marginBottom: theme.spacing(2),
  },
});

const EditMeetingLayoutWithPicklist = ({
  className,
  classes,
  onCloseEditModal,
}) => {
  const { householdResource } = useSimpleHousehold();
  const listDispatch = useListContextDispatch();
  const { resource, onSubmit, saving } = useMetaLayoutContext();
  const { logEvent } = useEventTrackingLogEvent();
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [cancelMeetingConfirmation, setCancelMeetingConfirmation] = useState(false);
  const [updateMeetingConfirmation, setUpdateMeetingConfirmation] = useState(false);
  const onCancelMeetingConfirmationOpen = () => setCancelMeetingConfirmation(true);
  const onCancelMeetingConfirmationClose = () => setCancelMeetingConfirmation(false);
  const onUpdateMeetingConfirmationOpen = () => setUpdateMeetingConfirmation(true);
  const onUpdateMeetingConfirmationClose = () => setUpdateMeetingConfirmation(false);

  const handleCancelMeeting = async () => {
    logEvent('Cancel Meeting in Mission Control');
    try {
      setConfirmationLoading(true);
      await resource.relationship(fr.relationships.calendarEvent.name).link('related').delete();
      const [updatedResource] = await resource.link('self').get();
      listDispatch({ type: 'updateResource', resource: updatedResource });
      onCloseEditModal();
      displaySuccess('Successfully cancelled meeting');
    } catch (err) {
      displayErrorMessage(err, 'Failed to cancel meeting');
    }
  };

  const handleShareRescheduleLink = () => {
    logEvent('Share Reschedule Meeting Link');
    try {
      const availabilityPeriodLink = resource.relationship(fr.relationships.rescheduleMeeting.name).link('related').url();
      const schedulerLink = `${GetAppUrl()}${clientSchedulerUri}`;
      const schedulerUrl = new URL(schedulerLink);
      schedulerUrl.searchParams.set('link', availabilityPeriodLink);
      copyToClipboard(schedulerUrl.toString());
      displaySuccess('Copied link to clipboard');
    } catch (err) {
      displayErrorMessage(err, 'Failed to copy link');
    }
  };


  const rescheduleRelationship = resource.relationship(fr.relationships.rescheduleMeeting.name);
  const handleRescheduleClick = () => {
    if (rescheduleRelationship) {
      onCloseEditModal();
      logEvent('Clicked Reschedule Meeting');
      history.push(advisorHouseholdSchedulerUri(householdResource.id(), {
        link: rescheduleRelationship.link('related').url({
          tz: moment.tz.guess(),
        }),
      }));
    }
  };

  const handleUpdateMeeting = () => {
    if (resource.attr('isCancelled') === 'false' && resource.attr('meetingHeldStatus') !== '') {
      onUpdateMeetingConfirmationOpen();
    } else {
      onSubmit(resource);
    }
  };

  const handleConfirmUpdateMeeting = () => {
    setConfirmationLoading(true);
    onSubmit(resource);
  };


  return (
    <Box
      className={classNames(classes.root, className)}
    >
      <Typography
        variant={'h4'}
        weight={'bold'}
        className={classes.meetingTitle}
      >
        {resource.attr('title')}
      </Typography>
      <div className={classes.meetingSubtitleContainer}>
        <Typography className={classes.meetingSubtitle}>
          Meeting Details
        </Typography>
        <div className={classes.actionContainer}>
          <CancelMeetingButton onClick={onCancelMeetingConfirmationOpen} disabled={resource.attr('isCancelled') === 'true'} />
          <ShareRescheduleMeetingLinkButton onClick={handleShareRescheduleLink} disabled={resource.attr('isCancelled') === 'true'} />
        </div>
      </div>
      <MetaControlField attr="startTime" onClick={handleRescheduleClick} isCancelled={resource.attr('isCancelled') === 'true' || !rescheduleRelationship} />
      <MetaControlField attr="meetingType" />
      <MetaControlField attr="meetingHeldStatus" shouldDisableOption={() => resource.attr('isCancelled') === 'true'} />
      <RoundedButton
        className={classes.referrerAgreementButton}
        onClick={handleUpdateMeeting}
        variant={'contained'}
        color={'primary'}
        fullWidth
        disableElevation
        size={DS_SIZE_MAP.sm}
        isLoading={saving}
      >
          Save
      </RoundedButton>
      {cancelMeetingConfirmation && (
        <ConfirmationDialog
          loading={confirmationLoading}
          message="Cancelling a meeting cannot be undone."
          onCancel={onCancelMeetingConfirmationClose}
          onConfirm={handleCancelMeeting}
        />
      )}
      {updateMeetingConfirmation && (
        <ConfirmationDialog
          loading={confirmationLoading}
          message="Marking a meeting as Client No Show or Client Cancelled will cancel the meeting and cannot be undone."
          onCancel={onUpdateMeetingConfirmationClose}
          onConfirm={handleConfirmUpdateMeeting}
        />
      )}
    </Box>
  );
};

EditMeetingLayoutWithPicklist.defaultProps = {
  className: '',
};

EditMeetingLayoutWithPicklist.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onCloseEditModal: PropTypes.func.isRequired,
};

export default withStyles(styles)(EditMeetingLayoutWithPicklist);
